import { format, parseISO } from "date-fns";

const CURRENCY_SYMBOLS: Record<string, string> = {
  MUR: "Rs",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

export function currencySymbol(currency?: string | null): string {
  const c = (currency || "MUR").toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? c;
}

/**
 * Money, thousands-separated, prefixed with the currency symbol.
 * Defaults to MUR (Rs). Ad spend/cost use the ad account's native currency
 * (USD/EUR); revenue is always MUR.
 */
export function money(
  n: number | null | undefined,
  currency: string = "MUR",
  decimals = 0
): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${currencySymbol(currency)} ${n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function num(n: number | null | undefined, decimals = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** ROAS as a multiple, e.g. 5.2×. Null => not logged. */
export function roasX(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}×`;
}

/** Signed percentage from a fraction: 0.153 => "+15%". Null => "—". */
export function signedPct(fraction: number | null | undefined): string {
  if (fraction == null || Number.isNaN(fraction)) return "—";
  const pct = Math.round(fraction * 100);
  return `${pct > 0 ? "+" : ""}${pct}%`;
}

export function shortDate(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d");
  } catch {
    return iso;
  }
}

export function longDate(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return iso;
  }
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "MMM d, HH:mm");
  } catch {
    return iso;
  }
}

/** Human label for a conversion type key, e.g. "initiate_checkout" -> "Checkouts". */
const CONV_LABELS: Record<string, string> = {
  purchase: "Purchases",
  initiate_checkout: "Checkouts",
  add_to_cart: "Add to cart",
  lead: "Leads",
  complete_registration: "Registrations",
  messaging_conversation_started: "Messaging convos",
  landing_page_view: "Landing views",
  link_click: "Link clicks",
};

export function conversionLabel(type: string | null | undefined): string {
  if (!type) return "Conversions";
  return CONV_LABELS[type] ?? type;
}
